/**
 * Náhledová plocha karty (proposal 003 §6).
 * Odpočívá na hlavní fotce (má-li produkt reálnou galerii), jinak na křídovém
 * glyfu. Na hover/focus (jen zařízení s jemným kurzorem)
 * se větví dle typu obsahu:
 *  - VIDEO s `teaser_url`: přehraje němou smyčku přes poster (s úklidem při odchodu),
 *  - VIDEO bez `teaser_url`: dokreslí kreslený poster (draw-in), NE fotogalerie,
 *  - jinak jsou-li `thumbs`: crossfade slideshow (intent 120 ms, snímek 1200 ms,
 *    crossfade 350 ms, max 4, křídové tečky) a na další snímek přejde teprve, až je
 *    reálně načtený (onload), aby na pomalé síti nebliklo,
 *  - jinak dokreslí glyf (draw-in, jednou za hover).
 * Respektuje `prefers-reduced-motion` (statická výměna, žádný cyklus) a dotyk
 * (žádný sticky hover, jen štítek počtu náhledů). Vizuál je `aria-hidden`.
 */

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Images, Play } from 'lucide-react';
import type { ProductCard as ProductCardData } from '../lib/api';
import { DWELL_MS, EASE, FADE_S, INTENT_MS, MAX_FRAMES } from '../lib/motion';
import { useCanHover } from '../lib/useMediaQuery';
import ProductPreview from './ProductPreview';

function ChalkDots({ count, active }: { count: number; active: number }) {
  return (
    <div
      className="absolute inset-x-0 bottom-2 z-[2] flex justify-center gap-1.5"
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className={`h-1 w-1 rounded-full ${i === active ? 'bg-accent' : 'bg-chalk/50'}`}
        />
      ))}
    </div>
  );
}

/** `active` řídí ProductCard (hover i focus na kartě) kvůli klávesové paritě. */
export default function CardPreview({
  product,
  active,
}: {
  product: ProductCardData;
  active: boolean;
}) {
  const thumbs = (product.thumbs ?? []).slice(0, MAX_FRAMES);
  const galleryCount = product.gallery_count ?? 0;
  const teaser = product.teaser_url ?? null;
  const isVideo = product.content_type === 'VIDEO';
  // Má reálné fotky galerie? Pak karta odpočívá na hlavní fotce (thumbs[0]), ne na glyfu.
  const hasImage = !isVideo && thumbs.length > 0;

  const canHover = useCanHover();
  const reduce = useReducedMotion() ?? false;

  const [frame, setFrame] = useState(-1); // -1 = klid (glyf)
  const [teaserOn, setTeaserOn] = useState(false);
  const [drawKey, setDrawKey] = useState(0); // změna = přehraj draw-in

  const videoRef = useRef<HTMLVideoElement>(null);
  const thumbsKey = thumbs.join('|');

  // S3: úplný úklid teaseru: zastav, přetoč, uvolni zdroj (žádné přehrávání na pozadí).
  const stopVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    v.removeAttribute('src');
    v.load();
  };

  useEffect(() => {
    if (!active || !canHover) {
      stopVideo();
      setFrame(-1);
      setTeaserOn(false);
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    // Preload snímků + čekání na skutečné načtení (S2: žádné bliknutí).
    const imgs = thumbs.map((src) => {
      const im = new Image();
      im.src = src;
      return im;
    });
    const ready = (i: number) => !!imgs[i] && imgs[i].complete && imgs[i].naturalWidth > 0;
    const load = (i: number) =>
      new Promise<void>((resolve) => {
        const im = imgs[i];
        if (!im || ready(i)) return resolve();
        im.onload = () => resolve();
        im.onerror = () => resolve(); // chybu neblokujeme, jen nepočkáme donekonečna
      });

    void (async () => {
      await sleep(INTENT_MS);
      if (cancelled) return;

      // VIDEO se větví zvlášť: teaser, nebo kreslený poster (nikdy fotoslideshow).
      if (isVideo) {
        if (teaser && !reduce) {
          setTeaserOn(true);
        } else if (!reduce) {
          setDrawKey((k) => k + 1);
        }
        return;
      }

      if (thumbs.length > 0) {
        await load(0);
        if (cancelled) return;
        setFrame(0);
        let cur = 0;
        while (thumbs.length > 1 && !reduce && !cancelled) {
          await sleep(DWELL_MS);
          if (cancelled) return;
          const next = (cur + 1) % thumbs.length;
          await load(next); // přejdi až na reálně načtený snímek (S2)
          if (cancelled) return;
          setFrame(next);
          cur = next;
        }
      } else if (!reduce) {
        setDrawKey((k) => k + 1); // draw-in glyfu (jen bez fotek)
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
      stopVideo();
      setFrame(-1);
      setTeaserOn(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, canHover, reduce, teaser, thumbsKey, isVideo]);

  const fade = reduce ? { duration: 0 } : { duration: FADE_S, ease: EASE };

  return (
    <div className="absolute inset-0" aria-hidden="true">
      {/* Základ v klidu: má-li produkt reálné fotky, leží na hlavní fotce (thumbs[0]).
          Náhledová plocha je čtvercová jako prodejní vizuál (1:1), takže se vejde celý
          bez ořezu. Slideshow na hover startuje také snímkem 0, takže přechod z klidu
          je bezešvý (žádné bliknutí na glyf). Bez fotek klasický glyf. */}
      {hasImage ? (
        <img
          src={thumbs[0]}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center p-4">
          <ProductPreview
            key={drawKey}
            subject={product.subject}
            contentType={product.content_type}
            productId={product.id}
            animateDraw={drawKey > 0}
            className="h-auto max-h-[82%] w-[78%]"
          />
        </div>
      )}

      {/* Video: play tlačítko zůstává (proposal 003 §6.4). */}
      {isVideo && (
        <span className="pointer-events-none absolute left-1/2 top-1/2 z-[2] grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-chalk/95 shadow-lg transition-transform motion-safe:group-hover:scale-[1.06]">
          <Play className="ml-0.5 h-5 w-5 text-board" strokeWidth={2} fill="currentColor" />
        </span>
      )}

      {/* Video teaser smyčka. */}
      <AnimatePresence>
        {teaserOn && teaser && (
          <motion.video
            key="teaser"
            ref={videoRef}
            src={teaser}
            muted
            loop
            playsInline
            autoPlay
            preload="none"
            className="absolute inset-0 z-[1] h-full w-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fade}
          />
        )}
      </AnimatePresence>

      {/* Slideshow náhledů (crossfade). */}
      <AnimatePresence>
        {frame >= 0 && thumbs[frame] && (
          <motion.img
            key={frame}
            src={thumbs[frame]}
            alt=""
            className="absolute inset-0 z-[1] h-full w-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fade}
          />
        )}
      </AnimatePresence>

      {frame >= 0 && thumbs.length > 1 && <ChalkDots count={thumbs.length} active={frame} />}

      {/* Mobil (bez hoveru): štítek počtu náhledů. */}
      {!canHover && galleryCount > 1 && (
        <span className="absolute bottom-3 right-3 z-[2] inline-flex items-center gap-1 rounded-full bg-board-deep/60 px-2 py-0.5 text-[0.65rem] font-bold text-chalk backdrop-blur">
          <Images className="h-3 w-3" strokeWidth={2.4} />
          {galleryCount}
        </span>
      )}
    </div>
  );
}
